import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import { useFocusEffect } from 'expo-router/react-navigation';
import NeverMissAPaymentBottomSheet from 'features/payOverTime/screens/Autopay/NeverMissAPayment/NeverMissAPaymentBottomSheet';
import usePayOverTimeLoanDetails from 'features/payOverTime/screens/LoanDetails/hooks/usePayOverTimeLoanDetails';
import LoanViewHeader from 'features/payOverTime/screens/LoanDetails/LoanViewHeader';
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import TransactionsSectionList from 'shared/components/TransactionSectionList/TransactionSectionList';
import Illustration from 'shared/components/vxl/Illustration/Illustration';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

const LoanDetails = () => {
  const router = useRouter();
  const { bottom } = useSafeAreaInsets();
  const { loanId } = useLocalSearchParams<{ loanId: string }>();
  const hasFocusedBefore = useRef(false);
  const autopayBottomSheetRef = useRef<BottomSheetModal>(null);

  const handleShowNeverMissPaymentSheet = useCallback(() => {
    autopayBottomSheetRef.current?.present();
  }, []);

  const {
    resultsObj: {
      banner,
      loanTitle,
      amountDue,
      dueDate,
      isAutoPayEnabled,
      remainingBalance,
      transactionsMap,
    },
    error,
    fetching,
    refetch,
  } = usePayOverTimeLoanDetails(
    loanId,
    'cache-and-network',
    handleShowNeverMissPaymentSheet,
  );

  useFocusEffect(
    useCallback(() => {
      if (hasFocusedBefore.current) {
        refetch();
      } else {
        hasFocusedBefore.current = true;
      }
    }, [refetch]),
  );

  const { t: translatedText } = useTranslation('payOverTime', {
    keyPrefix: 'loanDetails',
  });

  useViewDidFocusScreenAnalyticsEvent({
    key: '/pay-over-time/loan-details',
  });

  const dismissToTab = useDismissToRootTab();

  const goHome = () => {
    dismissToTab(Tabs.HOME);
  };

  const handleTurnOnAutopay = () => {
    if (!loanId) {
      return;
    }

    autopayBottomSheetRef.current?.close();
    router.push({
      pathname: '/pay-over-time/autopay',
      params: {
        loanId,
      },
    });
  };

  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      <Stack.Screen
        options={{
          title: loanTitle ?? '',
          headerBackVisible: !error,
          headerShown: !error,
          gestureEnabled: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <ScreenTransitionOverlay
        isLoading={fetching}
        LoadingComponent={<FullScreenLoading />}
        ErrorComponent={
          error ? (
            // ToDo: Replace later with BNPL specific error screen
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={translatedText('error.title')}
              messageBody={translatedText('error.body')}
              primaryButton={{
                title: translatedText('error.button'),
                onPress: goHome,
              }}
            />
          ) : null
        }
      />
      {transactionsMap && transactionsMap.length > 0 ? (
        <TransactionsSectionList
          transactions={transactionsMap}
          header={{
            id: 'header',
            render: () => (
              <LoanViewHeader
                banner={banner || null}
                dueDate={dueDate || null}
                isAutoPayEnabled={isAutoPayEnabled ?? false}
                remainingBalance={remainingBalance}
                paymentDue={amountDue || null}
                loanId={loanId}
              />
            ),
          }}
        />
      ) : (
        <ScrollView>
          <LoanViewHeader
            banner={banner || null}
            dueDate={dueDate || null}
            isAutoPayEnabled={isAutoPayEnabled ?? false}
            remainingBalance={remainingBalance}
            paymentDue={amountDue || null}
            loanId={loanId}
          />
          <View style={styles.emptyStateContainer}>
            <View style={styles.image}>
              <Illustration illustration="COINS_C" />
            </View>
            <BodyBaseText>{translatedText('emptyState')}</BodyBaseText>
          </View>
        </ScrollView>
      )}
      <NeverMissAPaymentBottomSheet
        ref={autopayBottomSheetRef}
        onTurnOnAutopay={handleTurnOnAutopay}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  emptyStateContainer: {
    alignItems: 'center',
    flex: 1,
  },
  image: {
    height: 131,
    width: 131,
  },
});

export default LoanDetails;
